if (!customElements.get('scrolling-product-spotlight')) {
  // Single Lenis-driving ticker for the page. When set, gsap.ticker is the one
  // frame-locked loop advancing both Lenis's scroll and the scrubbed timeline.
  let lenisTicker = null;

  customElements.define(
    'scrolling-product-spotlight',
    class ScrollingProductSpotlight extends HTMLElement {
      connectedCallback() {
        this.classList.add('js-animations-enabled');

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        } else {
          requestAnimationFrame(() => this.init());
        }
      }

      disconnectedCallback() {
        if (this.scrollTriggerInstance) {
          this.scrollTriggerInstance.kill();
          this.scrollTriggerInstance = null;
        }
        if (this._lenisInstance && this._lenisScrollHandler) {
          this._lenisInstance.off('scroll', this._lenisScrollHandler);
        }
        if (this._ownsLenisBinding) {
          if (lenisTicker) gsap.ticker.remove(lenisTicker);
          lenisTicker = null;
          window.lenisDrivenExternally = false; // hand the rAF loop back to the theme
          this._ownsLenisBinding = false;
        }
        this._lenisInstance = null;
        this._lenisScrollHandler = null;
      }

      init() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.showFinalState();
          return;
        }

        try {
          if (window.matchMedia('(max-width: 989px)').matches) {
            this.initMobile();
          } else {
            this.initDesktop();
          }
        } catch (e) {
          console.warn('[scrolling-product-spotlight] Animation init failed, showing static fallback.', e);
          this.showFinalState();
        }
      }

      // Sync ScrollTrigger to Lenis and make gsap.ticker the SINGLE loop that
      // advances Lenis. Two independent rAF loops (theme's + GSAP's) desync on
      // fast scroll and cause the pin to jump; one frame-locked loop fixes it.
      // lagSmoothing(0) stops GSAP jumping time forward on a slow frame. Bound
      // once per page (guarded by the module-level lenisTicker).
      bindLenis() {
        const lenis = window.lenis;
        if (!lenis || typeof lenis.on !== 'function') return;
        if (lenisTicker) return;

        window.lenisDrivenExternally = true;
        lenis.on('scroll', ScrollTrigger.update);
        lenisTicker = (time) => lenis.raf(time * 1000);
        gsap.ticker.add(lenisTicker);
        gsap.ticker.lagSmoothing(0);

        this._ownsLenisBinding = true;
        this._lenisInstance = lenis;
        this._lenisScrollHandler = ScrollTrigger.update;
      }

      initDesktop() {
        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
          this.showFinalState();
          return;
        }

        gsap.registerPlugin(ScrollTrigger);

        const scene = this.querySelector('.scrolling-product-spotlight__scene');
        const bgText = this.querySelector('.scrolling-product-spotlight__bg-text');
        const centerProduct = this.querySelector('.spotlight-product--center');
        const leftProduct = this.querySelector('.spotlight-product--left');
        const rightProduct = this.querySelector('.spotlight-product--right');
        const content = this.querySelector('.scrolling-product-spotlight__content');

        if (!scene) return;

        const scroller = window;

        this.bindLenis();

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: scene,
            scroller,
            start: 'top top',
            end: () => '+=' + (window.innerWidth < 990 ? window.innerHeight : window.innerHeight * 2),
            pin: true,
            pinSpacing: true,
            scrub: 0.6,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        this.scrollTriggerInstance = tl.scrollTrigger;

        // Text rises from low in the scene (starts in the masked-out lower zone so it's
        // hidden at pin-start, not parked on screen) up past centre into the upper portion.
        // Starting-y is the main knob for how "already visible" the text is when the pin engages.
        if (bgText) {
          tl.fromTo(
            bgText,
            { y: () => window.innerHeight * 0.4 },
            { y: () => window.innerWidth >= 990 ? -window.innerHeight * 0.15 : 0, duration: 0.35, ease: 'none' },
            0
          );
        }

        // Products start fully below the scene bottom so none are visible before animation
        if (centerProduct) {
          tl.fromTo(
            centerProduct,
            { y: () => window.innerHeight * 1.1, opacity: 1, rotation: 0 },
            { y: 0, opacity: 1, rotation: 3.5, duration: 0.35, ease: 'power2.out' },
            0.18
          );
        }

        if (leftProduct) {
          tl.fromTo(
            leftProduct,
            { y: () => window.innerHeight * 1.1, opacity: 1, rotation: 0 },
            { y: 0, opacity: 1, rotation: -7, duration: 0.35, ease: 'power2.out' },
            0.22
          );
        }

        if (rightProduct) {
          tl.fromTo(
            rightProduct,
            { y: () => window.innerHeight * 1.1, opacity: 1, rotation: 0 },
            { y: 0, opacity: 1, rotation: -10, duration: 0.35, ease: 'power2.out' },
            0.22
          );
        }

        // Content follows products with a short gap
        if (content) {
          tl.fromTo(content, { opacity: 0 }, { opacity: 1, duration: 0.06 }, 0.52);
          tl.fromTo(content, { y: 80 }, { y: 0, duration: 0.35, ease: 'power2.out' }, 0.52);
        }

        ScrollTrigger.refresh();
      }

      initMobile() {
        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
          this.showFinalState();
          return;
        }

        gsap.registerPlugin(ScrollTrigger);

        const scene = this.querySelector('.scrolling-product-spotlight__scene');
        const centerProduct = this.querySelector('.spotlight-product--center');
        const leftProduct = this.querySelector('.spotlight-product--left');
        const rightProduct = this.querySelector('.spotlight-product--right');
        const content = this.querySelector('.scrolling-product-spotlight__content');

        if (!scene) return;

        const scroller = window;

        this.bindLenis();

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: scene,
            scroller,
            start: 'top 40%',
            end: 'top top',
            pin: false,
            scrub: 0.6,
            invalidateOnRefresh: true,
          },
        });

        this.scrollTriggerInstance = tl.scrollTrigger;

        if (centerProduct) {
          tl.fromTo(
            centerProduct,
            { y: () => window.innerHeight * 0.5, opacity: 1, rotation: 0 },
            { y: 0, opacity: 1, rotation: 3.5, duration: 0.55, ease: 'power2.out' },
            0
          );
        }

        if (leftProduct) {
          tl.fromTo(
            leftProduct,
            { y: () => window.innerHeight * 0.5, opacity: 1, rotation: 0 },
            { y: 0, opacity: 1, rotation: -7, duration: 0.55, ease: 'power2.out' },
            0.05
          );
        }

        if (rightProduct) {
          tl.fromTo(
            rightProduct,
            { y: () => window.innerHeight * 0.5, opacity: 1, rotation: 0 },
            { y: 0, opacity: 1, rotation: -10, duration: 0.55, ease: 'power2.out' },
            0.05
          );
        }

        if (content) {
          tl.fromTo(content, { opacity: 0 }, { opacity: 1, duration: 0.1 }, 0.75);
          tl.fromTo(content, { y: 80 }, { y: 0, duration: 0.2, ease: 'power2.out' }, 0.75);
        }

        ScrollTrigger.refresh();
      }

showFinalState() {
        const products = this.querySelectorAll('.spotlight-product');
        const content = this.querySelector('.scrolling-product-spotlight__content');
        const bgText = this.querySelector('.scrolling-product-spotlight__bg-text');

        products.forEach((el) => {
          el.style.opacity = '1';
          if (el.classList.contains('spotlight-product--left')) el.style.transform = 'rotate(-7deg)';
          else if (el.classList.contains('spotlight-product--center')) el.style.transform = 'rotate(3.5deg)';
          else if (el.classList.contains('spotlight-product--right')) el.style.transform = 'rotate(-10deg)';
        });

        if (content) {
          content.style.opacity = '1';
          content.style.transform = 'none';
        }

        if (bgText) {
          bgText.style.transform = 'none';
          bgText.style.opacity = '1';
        }
      }
    }
  );
}
